import type { Ref, ShallowRef } from 'vue';
import type Map from 'ol/Map';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import type VectorLayer from 'ol/layer/Vector';
import type VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';

import { useBaseStationsStore } from '@/stores/baseStations';

import { FEATURE_TYPE } from '@/components/map/consts/mapFeatureConsts';
import type { MapFeature } from '@/components/map/features/MapFeatures';
import type { FeatureType, MapMode, BaseStationStyleFeatureData } from '@/components/map/types/mapFeatureTypes';
import MapStyles from '@/components/map/features/MapStyles';
import MapHelpers from '@/components/map/utils/MapHelpers';
import {
	asTypedMapFeature,
	isTypedMapFeatureOfType,
} from '@/components/map/utils/mapFeatureGuards';


export type OlMapWithFeaturePick = Pick<Map, 'forEachFeatureAtPixel'>;


type HoverHandler = (hovered: MapFeature) => void;

type UpdateTooltipCoords = (hovered: MapFeature) => void;


const updateLabels = (
	vectorLayer: VectorLayer<VectorSource>,
	focusedMountpoint: string,
	mapMode: MapMode,
	fillCoverageArea: boolean,
): void => {
	const labelOpacity = MapStyles.getBaseStationLabelOpacity(mapMode, fillCoverageArea);
	const source = vectorLayer.getSource();
	if (source == null) {
		return;
	}
	source.forEachFeature(feature => {
		const typed = asTypedMapFeature(feature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.baseStationLabel)) {
			return;
		}
		const featureData = typed.featureData;
		feature.setStyle(MapStyles.getLabelStyle(
			featureData.mountpointDisplay,
			MapHelpers.checkIsFocusedMountpoint(featureData, focusedMountpoint),
			labelOpacity,
		));
	});
};


const forEachBaselineLinkById = (
	vectorLayer: ShallowRef<VectorLayer<VectorSource> | null>,
	baselineId: number,
	hovered: boolean,
): void => {
	const layer = vectorLayer.value;
	if (layer == null) {
		return;
	}
	const source = layer.getSource();
	if (source == null) {
		return;
	}
	source.forEachFeature(feature => {
		const typed = asTypedMapFeature(feature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.baselineLinkLine)) {
			return;
		}
		const featureData = typed.featureData;
		if (featureData.id === baselineId) {
			feature.setStyle(MapStyles.getBaselineLinkStyle(featureData, hovered));
		}
	});
};


const useProcessHoverEvent = (
	olMap: Ref<OlMapWithFeaturePick | null>,
	hovered: ShallowRef<MapFeature | null>,
	vectorLayer: ShallowRef<VectorLayer<VectorSource> | null>,
	updateTooltipCoords: UpdateTooltipCoords,
	allowedHoverTypes: Readonly<Ref<readonly FeatureType[]>>,
	focusedMountpoint: Ref<string>,
	fillCoverageArea: Ref<boolean>,
) => {
	const getIsFocused = (featureData: BaseStationStyleFeatureData): boolean => (
		MapHelpers.checkIsFocusedMountpoint(featureData, focusedMountpoint.value)
	);
	const baseStationsStore = useBaseStationsStore();

	const processUnhoverBaseStation: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.baseStation)) {
			return;
		}
		const featureData = typed.featureData;
		hoveredFeature.setStyle(MapStyles.getBaseStationStyleMain(
			baseStationsStore.mode,
			FEATURE_TYPE.baseStation,
			featureData,
			false,
			false,
			getIsFocused(featureData),
		));
	};

	const processUnhoverVrsStation: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.vrsStation)) {
			return;
		}
		const featureData = typed.featureData;
		hoveredFeature.setStyle(MapStyles.getBaseStationStyleMain(
			baseStationsStore.mode,
			FEATURE_TYPE.vrsStation,
			featureData,
			false,
			false,
			false,
		));
	};

	const processUnhoverGetterActive: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.getterActive)) {
			return;
		}
		const featureData = typed.featureData;
		hoveredFeature.setStyle(MapStyles.getActiveGetterStyle(
			featureData.service,
			featureData.connectedBaseStation,
			false,
		));
	};

	const processUnhoverGetterFailed: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.getterFailed)) {
			return;
		}
		const featureData = typed.featureData;
		hoveredFeature.setStyle(MapStyles.getFailedGetterStyle(
			featureData.service,
			featureData.is_new,
			false,
		));
	};

	const processUnhoverBaselineLinkLine: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.baselineLinkLine)) {
			return;
		}
		const featureData = typed.featureData;
		hoveredFeature.setStyle(MapStyles.getBaselineLinkStyle(featureData, false));
	};

	const processUnhoverBaselineLinkSelfCircle: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.baselineLinkSelfCircle)) {
			return;
		}
		const featureData = typed.featureData;
		hoveredFeature.setStyle(MapStyles.getBaselineSelfCircleStyle(featureData, false));
	};

	const processUnhoverBaselineLinkArrowHead: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.baselineLinkArrowHead)) {
			return;
		}
		forEachBaselineLinkById(vectorLayer, typed.featureData.id, false);
	};

	const processUnhoverCnetConstructorGridpoint: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.cnetConstructorGridpoint)) {
			return;
		}
		const featureData = typed.featureData;
		hoveredFeature.setStyle(MapStyles.getCnetConstructorGridpointStyle(
			featureData.selected,
			false,
			featureData.gpIndex,
		));
	};

	const processUnhoverRoverHistoryCurrent: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.roverHistoryCurrent)) {
			return;
		}
		const featureData = typed.featureData;
		hoveredFeature.setStyle(MapStyles.getRoverHistoryRoverStyle(featureData.quality, true, false));
	};

	const processUnhoverRoverHistoryFragment: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.roverHistoryFragment)) {
			return;
		}
		const featureData = typed.featureData;
		hoveredFeature.setStyle(MapStyles.getRoverHistoryFragmentLineStyle(featureData.quality, false));
	};

	const processUnhoverQualityRectangle: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.qualityRectangle)) {
			return;
		}
		const featureData = typed.featureData;
		hoveredFeature.setStyle(MapStyles.getQualityRectangleStyle(featureData.q_color_value, false));
	};

	const UNHOVER_FUNCTIONS: Partial<Record<FeatureType, HoverHandler>> = {
		[FEATURE_TYPE.baseStation]: processUnhoverBaseStation,
		[FEATURE_TYPE.vrsStation]: processUnhoverVrsStation,
		[FEATURE_TYPE.getterActive]: processUnhoverGetterActive,
		[FEATURE_TYPE.getterFailed]: processUnhoverGetterFailed,
		[FEATURE_TYPE.baselineLinkLine]: processUnhoverBaselineLinkLine,
		[FEATURE_TYPE.baselineLinkSelfCircle]: processUnhoverBaselineLinkSelfCircle,
		[FEATURE_TYPE.baselineLinkArrowHead]: processUnhoverBaselineLinkArrowHead,
		[FEATURE_TYPE.cnetConstructorGridpoint]: processUnhoverCnetConstructorGridpoint,
		[FEATURE_TYPE.roverHistoryCurrent]: processUnhoverRoverHistoryCurrent,
		[FEATURE_TYPE.roverHistoryFragment]: processUnhoverRoverHistoryFragment,
		[FEATURE_TYPE.qualityRectangle]: processUnhoverQualityRectangle,
	};

	const processHoverBaseStation: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.baseStation)) {
			return;
		}
		const featureData = typed.featureData;
		updateTooltipCoords(hoveredFeature);
		hoveredFeature.setStyle(MapStyles.getBaseStationStyleMain(
			baseStationsStore.mode,
			FEATURE_TYPE.baseStation,
			featureData,
			true,
			false,
			getIsFocused(featureData),
		));
	};

	const processHoverVrsStation: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.vrsStation)) {
			return;
		}
		const featureData = typed.featureData;
		hoveredFeature.setStyle(MapStyles.getBaseStationStyleMain(
			baseStationsStore.mode,
			FEATURE_TYPE.vrsStation,
			featureData,
			true,
			false,
			false,
		));
	};

	const processHoverGetterActive: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.getterActive)) {
			return;
		}
		const featureData = typed.featureData;
		updateTooltipCoords(hoveredFeature);
		hoveredFeature.setStyle(MapStyles.getActiveGetterStyle(
			featureData.service,
			featureData.connectedBaseStation,
			true,
		));
	};

	const processHoverGetterFailed: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.getterFailed)) {
			return;
		}
		const featureData = typed.featureData;
		updateTooltipCoords(hoveredFeature);
		hoveredFeature.setStyle(MapStyles.getFailedGetterStyle(
			featureData.service,
			featureData.is_new,
			true,
		));
	};

	const processHoverBaselineLinkLine: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.baselineLinkLine)) {
			return;
		}
		const featureData = typed.featureData;
		hoveredFeature.setStyle(MapStyles.getBaselineLinkStyle(featureData, true));
	};

	const processHoverBaselineLinkSelfCircle: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.baselineLinkSelfCircle)) {
			return;
		}
		const featureData = typed.featureData;
		hoveredFeature.setStyle(MapStyles.getBaselineSelfCircleStyle(featureData, true));
	};

	const processHoverBaselineLinkArrowHead: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.baselineLinkArrowHead)) {
			return;
		}
		forEachBaselineLinkById(vectorLayer, typed.featureData.id, true);
	};

	const processHoverCnetConstructorGridpoint: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.cnetConstructorGridpoint)) {
			return;
		}
		const featureData = typed.featureData;
		hoveredFeature.setStyle(MapStyles.getCnetConstructorGridpointStyle(
			featureData.selected,
			true,
			featureData.gpIndex,
		));
	};

	const processHoverRoverHistoryCurrent: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.roverHistoryCurrent)) {
			return;
		}
		const featureData = typed.featureData;
		updateTooltipCoords(hoveredFeature);
		hoveredFeature.setStyle(MapStyles.getRoverHistoryRoverStyle(featureData.quality, true, true));
	};

	const processHoverRoverHistoryFragment: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.roverHistoryFragment)) {
			return;
		}
		const featureData = typed.featureData;
		updateTooltipCoords(hoveredFeature);
		hoveredFeature.setStyle(MapStyles.getRoverHistoryFragmentLineStyle(featureData.quality, true));
	};

	const processHoverQualityRectangle: HoverHandler = hoveredFeature => {
		const typed = asTypedMapFeature(hoveredFeature);
		if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.qualityRectangle)) {
			return;
		}
		const featureData = typed.featureData;
		updateTooltipCoords(hoveredFeature);
		hoveredFeature.setStyle(MapStyles.getQualityRectangleStyle(featureData.q_color_value, true));
	};

	const HOVER_FUNCTIONS: Partial<Record<FeatureType, HoverHandler>> = {
		[FEATURE_TYPE.baseStation]: processHoverBaseStation,
		[FEATURE_TYPE.vrsStation]: processHoverVrsStation,
		[FEATURE_TYPE.getterActive]: processHoverGetterActive,
		[FEATURE_TYPE.getterFailed]: processHoverGetterFailed,
		[FEATURE_TYPE.baselineLinkLine]: processHoverBaselineLinkLine,
		[FEATURE_TYPE.baselineLinkSelfCircle]: processHoverBaselineLinkSelfCircle,
		[FEATURE_TYPE.baselineLinkArrowHead]: processHoverBaselineLinkArrowHead,
		[FEATURE_TYPE.cnetConstructorGridpoint]: processHoverCnetConstructorGridpoint,
		[FEATURE_TYPE.roverHistoryCurrent]: processHoverRoverHistoryCurrent,
		[FEATURE_TYPE.roverHistoryFragment]: processHoverRoverHistoryFragment,
		[FEATURE_TYPE.qualityRectangle]: processHoverQualityRectangle,
	};

	const unhoverAnyThatIsHovered = (): void => {
		const hoveredFeature = hovered.value;
		if (hoveredFeature != null) {
			const typed = asTypedMapFeature(hoveredFeature);
			if (typed != null) {
				const processUnhoverFunction = UNHOVER_FUNCTIONS[typed.type];
				if (processUnhoverFunction != null) {
					processUnhoverFunction(hoveredFeature);
				}
			}
		}
		hovered.value = null;
	};

	const onHover = (event: MapBrowserEvent<UIEvent>): void => {
		const mapInstance = olMap.value;
		const layer = vectorLayer.value;
		if (mapInstance == null || layer == null) {
			return;
		}
		const hoveredLocal = mapInstance.forEachFeatureAtPixel(
			event.pixel,
			feature => {
				if (!(feature instanceof Feature)) {
					return undefined;
				}
				const typed = asTypedMapFeature(feature);
				if (typed != null && allowedHoverTypes.value.includes(typed.type)) {
					return feature;
				}
				return undefined;
			},
		);
		if (hovered.value != null && hoveredLocal !== hovered.value) {
			unhoverAnyThatIsHovered();
		}
		if (hoveredLocal != null) {
			const typed = asTypedMapFeature(hoveredLocal);
			if (typed != null) {
				const processHoverFunction = HOVER_FUNCTIONS[typed.type];
				if (processHoverFunction != null) {
					processHoverFunction(hoveredLocal);
					hovered.value = hoveredLocal;
				} else {
					hovered.value = null;
				}
			}
		}
		updateLabels(layer, focusedMountpoint.value, baseStationsStore.mode, fillCoverageArea.value);
	};

	return { onHover, unhoverAnyThatIsHovered };
};

export {
	useProcessHoverEvent,
};
